import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import useOrganizationInvitationDefinitions from '../../../../../hooks/api/useOrganizationInvitationDefinitions';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import OrganizationClientInvitationFields, {
    clientRecordToFormValue,
    formValueToCreateClientRecordInvitationRequest,
    getDefaultFormValue,
    OrganizationClientInvitationDisableProvisionProfileReason,
} from '../../../../shared/OrganizationClientInvitations/OrganizationClientInvitationFields';
import { Form } from '../../../../v2BaseComponents/Form';
import { useOrganizationClientRecords } from '../../../../../hooks/api/useOrganizationClientRecords';
import MaybePublicOrgMissingInfoAlert from '../../../../shared/MaybePublicOrgMissingInfoAlert';
import { getIntl } from '../../../../../utils/intl';
import { clientRecordStatusTypeGuard } from '../../../../../utils/clientRecordsUtil';

const intl = getIntl('profiles.addClientRecordInvitation', {
    send: 'Send',
});

export type AddClientRecordInvitationScreenOwnProps = {};

export type AddClientRecordInvitationScreenProps = AddClientRecordInvitationScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-client-record-invitation'>;
};

const AddClientRecordInvitationScreen: React.FC<AddClientRecordInvitationScreenProps> = ({ route }) => {
    const goBack = useGoBack();
    const { isLoadingClientRecord, clientRecord, isInvitingClientRecord, inviteClientRecord } =
        useOrganizationClientRecords({
            organizationClientRecordId: route.params.clientRecordId,
            onInviteClientRecordSuccess: goBack,
        });
    const { invitationDefinitions, isLoadingInvitationDefinitions } = useOrganizationInvitationDefinitions({});

    const initialValues = useMemo(
        () =>
            clientRecord
                ? clientRecordToFormValue(clientRecord, invitationDefinitions)
                : getDefaultFormValue(invitationDefinitions),
        [clientRecord, invitationDefinitions],
    );

    const isLoading =
        isLoadingClientRecord || isLoadingInvitationDefinitions || isInvitingClientRecord || !clientRecord;

    let disableProvisionProfileReason: undefined | OrganizationClientInvitationDisableProvisionProfileReason;
    if (clientRecord && clientRecordStatusTypeGuard.invitationRejected(clientRecord)) {
        disableProvisionProfileReason = 'inviting-rejected-client';
    } else if (
        clientRecord &&
        clientRecordStatusTypeGuard.notInvited(clientRecord) &&
        !!clientRecord.clientMedicalProfileId
    ) {
        disableProvisionProfileReason = 'inviting-previous-client';
    }

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <Form
                    fillContainer
                    actionContainerSubmit
                    initialValues={initialValues}
                    onSubmit={(values) => {
                        const createClientRecordInviteRequest = formValueToCreateClientRecordInvitationRequest(values);
                        inviteClientRecord({
                            organizationClientRecordId: route.params.clientRecordId,
                            request: createClientRecordInviteRequest,
                        });
                    }}
                    onCancel={goBack}
                    saveText={intl('send')}
                    saveIcon="Send"
                >
                    {({ values }) => (
                        <>
                            <MaybePublicOrgMissingInfoAlert />
                            <OrganizationClientInvitationFields
                                invitationDefinitions={invitationDefinitions}
                                value={values}
                                disableProvisionProfileReason={disableProvisionProfileReason}
                            />
                        </>
                    )}
                </Form>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(AddClientRecordInvitationScreen);
